import React, { SyntheticEvent, useMemo } from 'react'
import './header.scss'

import { ZinImage, ZinNav, ZinNavItem } from '@tnl/zin-react'
import { clsx } from 'clsx'
import { SCROLL_THRESHOLD } from '@constants'
import { withBase } from '@utils/basePath'
import { useBreakpoint } from '@shared/hooks'
import { useSiteContext } from '@contexts/MkSiteContext'
import { MkAccountMenu } from '../AccountMenu'
import { useTopNavMenuItems } from './useTopNavMenuItems'

export type NavMenuItem = {
  label: string
  href: string
  level: '1' | '2' | '3'
  hasSubnav?: boolean
  dataTestId: string
  children?: NavMenuItem[]
}

export type HeaderProps = {
  logoSrc?: string
  logoSrcDark?: string
  logoAlt?: string
  menuItems?: NavMenuItem[]
  promoHeader?: boolean
  scrolled?: boolean
  /** Logo only: no top nav or account menu (e.g. maintenance page). */
  logoOnly?: boolean
}

export const Header: React.FC<HeaderProps> = ({ logoSrc, logoSrcDark, logoAlt, menuItems, promoHeader = false, scrolled: externalScrolled, logoOnly = false }) => {
  const [scrolled, setScrolled] = React.useState(false)
  const [isAccountMenuOpen, setIsAccountMenuOpen] = React.useState(false)
  const [isNavOpen, setIsNavOpen] = React.useState(false)
  const { isMobile } = useBreakpoint()
  const { themeBrand } = useSiteContext()
  const apiMenuItems = useTopNavMenuItems(themeBrand)
  const isEbac = (themeBrand || '').toLowerCase() === 'ebac'

  // Use external scrolled state if provided (from SiteNav), otherwise use internal scroll detection
  const isScrolled = externalScrolled !== undefined ? externalScrolled : scrolled

  React.useEffect(() => {
    // Only set up internal scroll detection if external scrolled state is not provided
    if (externalScrolled === undefined) {
      const handleScroll = () => {
        setScrolled(window.scrollY > SCROLL_THRESHOLD)
      }

      window.addEventListener('scroll', handleScroll)

      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [externalScrolled])

  const handleAccountMenuToggle = React.useCallback((isOpen: boolean) => {
    setIsAccountMenuOpen(isOpen)
  }, [])

  const handleNavigationMenuState = (event: SyntheticEvent) => {
    if (!isMobile) return

    const path = event.nativeEvent.composedPath()

    // Check if button clicked or click outside of nav menu then toggle/close nav
    const insideNavigationAreaClick = path.some((node) => node instanceof Element && node.matches('.nav__menu'))
    const buttonClick = path.some((node) => node instanceof Element && node.matches('zin-icon-button.nav__hamburger'))

    if (buttonClick) {
      setIsNavOpen((prevState) => !prevState)
      return
    }
    if (!insideNavigationAreaClick) {
      setIsNavOpen(false)
    }
  }

  // Determine which logo to use
  // For promo headers: use dark logo when not scrolled, normal logo when scrolled
  // For non-promo headers: always use normal logo
  // EBAC: keep the "promo" logo treatment even on non-promo pages.
  const logoToUse = useMemo(() => {
    if (isEbac) {
      return promoHeader ? (isScrolled ? logoSrc : logoSrcDark) : logoSrcDark
    }

    return promoHeader ? (isScrolled ? logoSrc : logoSrcDark) : logoSrc
  }, [isEbac, promoHeader, isScrolled])

  const resolvedMenuItems = logoOnly ? null : (apiMenuItems ?? menuItems ?? null)

  // Function to render navigation items recursively
  const renderNavItem = (item: NavMenuItem): React.ReactNode => {
    const navItemProps = {
      href: withBase(item.href),
      level: item.level,
      'data-testid': item.dataTestId,
      ...(item.hasSubnav && { hasSubnav: true }),
    }

    if (item.children && item.children.length > 0) {
      return (
        <ZinNavItem
          key={item.dataTestId}
          {...navItemProps}>
          {item.label}
          {item.children.map((child) => (
            <ZinNavItem
              key={child.dataTestId}
              href={withBase(child.href)}
              slot="subnav"
              level={child.level}
              data-testid={child.dataTestId}>
              {child.label}
              {child.children?.map((grandChild) => (
                <ZinNavItem
                  key={grandChild.dataTestId}
                  href={withBase(grandChild.href)}
                  slot="subnav"
                  level={grandChild.level}
                  data-testid={grandChild.dataTestId}>
                  {grandChild.label}
                </ZinNavItem>
              ))}
            </ZinNavItem>
          ))}
        </ZinNavItem>
      )
    }

    return (
      <ZinNavItem
        key={item.dataTestId}
        {...navItemProps}>
        {item.label}
      </ZinNavItem>
    )
  }

  return (
    <header
      className={clsx('header w-full flex row space-between', {
        'header--scrolled': isScrolled,
        'header--account-menu-open': isAccountMenuOpen && !logoOnly,
        'header--nav-open': isNavOpen,
        'header--mobile': isMobile,
        'header--logo-only': logoOnly,
      })}
      onClick={handleNavigationMenuState}>
      <div className="header__inner">
        <div className="header__logo-search">
          <a
            className="header__logo-link"
            id="navLogo"
            data-testid="mk-nav-logo"
            href={withBase('/')}>
            <ZinImage
              src={logoToUse}
              alt={logoAlt}
              className="header__logo-image"
            />
          </a>
        </div>

        {resolvedMenuItems && resolvedMenuItems.length > 0 ? <ZinNav transparent>{resolvedMenuItems.map(renderNavItem)}</ZinNav> : null}

        {!logoOnly && (
          <MkAccountMenu
            className="mkAccountMenu"
            kind=""
            resourceKey=""
            menuItemConfig={{
              id: 0,
              key: '',
              value: '',
              children: [],
            }}
            onMenuToggled={handleAccountMenuToggle}
          />
        )}
      </div>
    </header>
  )
}
